import React, { useRef, useState } from "react";
import { PlusIcon, SearchIcon } from "@heroicons/react/outline";
import useClickOutside from "./hooks/useClickOutside";
import LinkedItemList from "./LinkedItemList";

export default function LinkedItemsField({
  id,
  value = [],
  onChange,
  options = [],
  toItem = option => ({ id: option.id, title: option.label }),
  missingTitle = itemId => `#${itemId}`,
  reorderable = false,
  itemHref,
  onOpen,
  withImages,
  searchLabel,
  searchPlaceholder = searchLabel,
  noMatchesText = term => `Nothing matches "${term}"`,
  allAddedText = "Everything is already added",
  emptyText,
  removeTitle,
  hint,
  droppableId = id,
  listClassName,
  renderMenuFooter
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const searchRef = useRef();
  useClickOutside(searchRef, () => setIsOpen(false));

  const optionById = new Map(options.map(option => [option.id, option]));
  const items = value.map(itemId =>
    optionById.has(itemId)
      ? toItem(optionById.get(itemId))
      : { id: itemId, title: missingTitle(itemId) }
  );
  const term = searchTerm.trim().toLowerCase();
  const matches = options.filter(
    option => !value.includes(option.id) && toItem(option).title.toLowerCase().includes(term)
  );

  const menuFooter = renderMenuFooter?.({
    searchTerm,
    closeMenu: () => {
      setSearchTerm("");
      setIsOpen(false);
    }
  });

  const add = option => {
    onChange([...value, option.id]);
    setSearchTerm("");
    setActiveIndex(0);
    setIsOpen(false);
  };

  const onKeyDown = e => {
    if (e.key === "Enter") {
      e.preventDefault();
      const option = matches[activeIndex] || matches[0];
      if (option) add(option);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setIsOpen(true);
      setActiveIndex(i => Math.min(i + 1, matches.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  return (
    <div className="mt-1">
      <div className="relative" ref={searchRef}>
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        <input
          id={id}
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={`${id}-options`}
          value={searchTerm}
          onChange={e => {
            setSearchTerm(e.target.value);
            setActiveIndex(0);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={searchPlaceholder}
          aria-label={searchLabel}
          autoComplete="off"
          className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md sm:text-sm focus:outline-none focus:border-blue-300"
        />
        {isOpen && (
          <ul
            id={`${id}-options`}
            role="listbox"
            className="absolute z-50 mt-1 w-full max-h-60 overflow-auto bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 py-1 text-sm"
          >
            {matches.map((option, index) => (
              <li key={option.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={index === activeIndex}
                  onClick={() => add(option)}
                  onMouseEnter={() => setActiveIndex(index)}
                  className={`w-full flex items-center justify-between px-3 py-2 text-left ${
                    index === activeIndex ? "bg-blue-50" : ""
                  }`}
                >
                  <span className="truncate">{toItem(option).title}</span>
                  <PlusIcon className="w-4 h-4 text-gray-400" />
                </button>
              </li>
            ))}
            {!matches.length && (
              <li className="px-3 py-2 text-gray-500">
                {searchTerm ? noMatchesText(searchTerm) : allAddedText}
              </li>
            )}
            {menuFooter && <li className="border-t border-gray-100">{menuFooter}</li>}
          </ul>
        )}
      </div>
      {hint && items.length > 0 && <p className="mt-3 mb-2 text-xs text-gray-500">{hint}</p>}
      <LinkedItemList
        droppableId={droppableId}
        items={items}
        withImages={withImages}
        onReorder={reorderable ? onChange : undefined}
        onRemove={itemId => onChange(value.filter(v => v !== itemId))}
        itemHref={itemHref}
        onOpen={onOpen}
        emptyText={emptyText}
        removeTitle={removeTitle}
        className={listClassName}
      />
    </div>
  );
}

export function MenuAction({ onClick, disabled, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full flex items-center px-3 py-2 text-left text-blue-600 hover:bg-blue-50 disabled:opacity-50"
    >
      <PlusIcon className="w-4 h-4 mr-2" />
      {children}
    </button>
  );
}
